import DraftsOutlined from "@mui/icons-material/DraftsOutlined";
import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import type { AppliedFilter } from "@/components/BlotterFilterBar";
import ThankYouNoteCard from "@/components/thankYouNotes/ThankYouNoteCard";
import ThankYouNoteRow from "@/components/thankYouNotes/ThankYouNoteRow";
import ThankYouNotesControls from "@/components/thankYouNotes/ThankYouNotesControls";
import WidgetEmptyState from "@/components/WidgetEmptyState";
import {
  THANK_YOU_NOTES_TITLE,
  TY_CSV_FILENAME,
  TY_EMPTY_SUBTITLE,
  TY_EMPTY_TITLE,
  TY_ERROR_MESSAGE,
  TY_EXPORT_ERROR,
  TY_FILTER_FIELD_IDS,
  TY_LOAD_MORE_LABEL,
  TY_LOADING_MORE_LABEL,
  TY_MAX_SEARCH_LENGTH,
  TY_NO_FAVORITES,
  TY_NO_MATCH,
  TY_RETRY_LABEL,
  TY_SEARCH_DEBOUNCE_MS,
  TY_VIEW_STORAGE_KEY,
  tyCountLabel,
  tyFavoritesCountLabel,
} from "@/constants/thankYouNotes";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { usePermissions } from "@/hooks/usePermissions";
import { THANK_YOU_NOTES_QUERY_KEY, useThankYouNotes } from "@/hooks/useThankYouNotes";
import type { ThankYouNotesQuery, ThankYouNotesView } from "@/interfaces/thankYouNotes";
import { buildCsv, downloadCsv } from "@/lib/csv";
import { filterByFavorites, thankYouNotesCsvColumns } from "@/utils/thankYouNotes-helper";
// `.ty-grid` (the grid container class below) lives in this sheet. The card imports it too,
// but relying on that is relying on the card staying a static import — a future `lazy()` would
// take the grid layout with it. Importing the same sheet twice is deduped by the bundler.
import "@/components/thankYouNotes/thankYouNoteCard.css";

/** Shared by the Retry and Load more buttons: same shape, different tone (see each call site). */
const PAGE_BUTTON_BASE =
  "bg-transparent rounded-md py-1.5 px-3 font-lato text-[13px] font-bold cursor-pointer transition-colors";

/**
 * View persisted for the session (precedent: the Drawer's `sidebarCollapsed`), validated on read
 * so a stale or hand-edited value can't leave the page in a view it doesn't know how to render.
 */
function readStoredView(): ThankYouNotesView {
  const saved = sessionStorage.getItem(TY_VIEW_STORAGE_KEY);
  return saved === "grid" || saved === "list" ? saved : "grid";
}

/**
 * Thank You Notes (GFG-233/234) — the client's feed of thank-you messages left by the
 * organisation's recipients, with search, campaign/date filters, favorites, a card/list toggle
 * and CSV export. The page `<h1>` comes from the Drawer topbar, not from here.
 *
 * Since GFG-234 search, campaign and the submitted range are SERVER-side: they travel as query
 * params, they are part of the TanStack key, and changing one refetches instead of re-filtering in
 * memory. The single exception is `favoritesOnly` — session state the server knows nothing about —
 * which `filterByFavorites` applies to the rows after they arrive.
 *
 * The feed is PAGED (the API caps a page at 100 rows): "Load more" appends the next page, the
 * counter reports the server's full filtered total, and the CSV export walks every page — three
 * numbers that must agree, which is why none of them is `visible.length`.
 */
export default function ThankYouNotesPage() {
  const { isGfg, isLoaded } = usePermissions();
  const queryClient = useQueryClient();

  const [view, setView] = useState<ThankYouNotesView>(readStoredView);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<AppliedFilter[]>([]);
  const [favOnly, setFavOnly] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // The input stays on `search` (instant, fully controlled); only the settled value reaches the
  // query, so a burst of typing costs one request instead of one per keystroke.
  const debouncedSearch = useDebouncedValue(search, TY_SEARCH_DEBOUNCE_MS);

  // The sf-bar speaks in `AppliedFilter[]`; the server speaks in query params. Campaign is
  // single-select (normalised in the controls), and the daterange's values are always
  // `[from, to]` with "" for an open-ended side — hence `|| null` rather than `?? null`.
  const campaign =
    filters.find((f) => f.fieldId === TY_FILTER_FIELD_IDS.campaign)?.values[0] || null;
  const submitted = filters.find((f) => f.fieldId === TY_FILTER_FIELD_IDS.submitted)?.values ?? [];
  const submittedFrom = submitted[0] || null;
  const submittedTo = submitted[1] || null;

  /**
   * What the server is asked for. A cleared filter is sent as `undefined`, never `""`: the API
   * matches `campaign` as an exact title, so a blank one would match nothing instead of meaning
   * "no filter". `favoritesOnly` is deliberately absent — it is browser session state.
   *
   * `cursor` is absent too: paging is the hook's business, and putting an offset in here would make
   * every "load more" a different cache key — the exact bug that would let rows from one filter
   * survive into another.
   *
   * Memoised for the export path's benefit (it closes over this object); TanStack hashes the key
   * structurally, so an unmemoised rebuild would still hit the same cache entry.
   */
  const query = useMemo<ThankYouNotesQuery>(
    () => ({
      search: debouncedSearch || undefined,
      campaign: campaign || undefined,
      submittedFrom: submittedFrom || undefined,
      submittedTo: submittedTo || undefined,
      // The campaign dropdown and the "no notes at all" check ride along on this same response,
      // so the first paint needs one request rather than three.
      includeFilterOptions: true,
    }),
    [debouncedSearch, campaign, submittedFrom, submittedTo],
  );

  /**
   * Hooks run before the `return` guards at the bottom of this component, so ungated a GfG admin
   * would fire this authenticated, row-scoped GET and only then be redirected to /gfg-dashboard.
   * `isLoaded` is half of the gate for the same reason: `isGfg` is false while permissions are
   * still loading, so enabling on `!isGfg` alone would let the request out in the very frame the
   * answer is not known yet.
   *
   * The condition is the exact complement of the two guards below, so every render that disables
   * the query returns `null` or a `<Navigate>` before reaching the feed. Nothing downstream sees a
   * disabled query: not the skeleton (an idle query has `isLoading: false`), not the "no notes yet"
   * empty state (`unfilteredTotal` stays `undefined`, never 0), not the error banner.
   */
  const {
    notes,
    total,
    campaignOptions,
    unfilteredTotal,
    isLoading,
    isFetching,
    isError,
    hasNextPage,
    isFetchingNextPage,
    loadMore,
    favoriteIds,
    toggleFavorite,
    fetchAllNotes,
  } = useThankYouNotes(query, { enabled: isLoaded && !isGfg });

  // `unfilteredTotal` ignores the active filters, which is what separates "this org has no notes
  // yet" from "nothing matched". Compared strictly to 0 so a response without the field falls
  // through to the filtered view and leaves the controls reachable. `!isError` keeps the old
  // precedence: a failure is reported as a failure, never dressed up as an empty organisation.
  const hasNoNotesInScope = unfilteredTotal === 0 && !isError;

  // A failed REFETCH keeps the last good pages (TanStack only leaves `data` undefined while the
  // query has never succeeded), so "erroring" and "having nothing to show" are not the same thing.
  // Only the second one is allowed to blank the feed; otherwise the banner goes above rows that
  // are still perfectly readable.
  const hasNothingToShow = isError && notes.length === 0;

  // Everything else arrived already filtered; favorites are the one rule the server cannot run.
  const visible = useMemo(
    () => filterByFavorites(notes, { favoritesOnly: favOnly, favoriteIds }),
    [notes, favOnly, favoriteIds],
  );

  // `placeholderData` holds the previous pages on screen while the next filter's page one loads, so
  // the feed never flashes empty mid-typing. Fading it says "this is the last answer, a new one is
  // coming" without throwing away the user's scroll position the way the skeleton would.
  // `isFetchingNextPage` is excluded: a "load more" ADDS to what is on screen, so dimming the rows
  // the user is already reading would be a lie about them.
  const isStale = isFetching && !isLoading && !isFetchingNextPage;

  function handleViewChange(next: ThankYouNotesView) {
    setView(next);
    sessionStorage.setItem(TY_VIEW_STORAGE_KEY, next);
  }

  /**
   * The term is a SERVER filter now, and the DTO caps it at `MAX_SEARCH_LENGTH` — anything longer
   * comes back 400, which used to take the whole feed (and the very input holding the offending
   * text) down with it, leaving Retry to re-issue the same bad request.
   *
   * Clamped at the setter rather than inside `query` so what is on screen equals what is searched:
   * a 250-character paste lands as its first 200 characters instead of silently searching a
   * different string than the box shows. It is NOT a `maxLength` on `BlotterFilterBar` — that bar
   * is shared by 11 surfaces that have nothing to do with this endpoint's limit.
   */
  function handleSearchChange(next: string) {
    // `slice` counts UTF-16 CODE UNITS, so a cut that lands between the two halves of an emoji's
    // surrogate pair leaves a dangling HIGH surrogate. Axios's param serializer runs the term
    // through `encodeURIComponent`, which THROWS on a lone surrogate — the request never leaves the
    // browser and the user is told to check a connection that was never used. Dropping that orphan
    // half only ever shortens the value, so the DTO's `max(200)` still passes.
    const clamped = next.slice(0, TY_MAX_SEARCH_LENGTH);
    setSearch(/[\uD800-\uDBFF]$/.test(clamped) ? clamped.slice(0, -1) : clamped);
  }

  function handleRetry() {
    void queryClient.invalidateQueries({ queryKey: THANK_YOU_NOTES_QUERY_KEY });
  }

  async function handleExport() {
    setIsExporting(true);
    setExportError(null);
    try {
      // The filters run server-side now, and the feed holds only the pages the user asked for, so
      // exporting `notes` would hand back a file that looks complete and is not. `fetchAllNotes`
      // walks every page of the query the feed is currently showing, so the CSV is exactly
      // "everything matching what's on screen".
      const all = await fetchAllNotes();
      // `thankYouNotesCsvColumns` reads `row.favorite`, which still carries whatever the API sent
      // (false on every GET row) — hydrate it from the session's Set so the exported column matches
      // what the user sees. The Set is keyed by id, so the Favorites toggle narrows the whole
      // export, not just the pages on screen.
      const hydrated = filterByFavorites(all, { favoritesOnly: favOnly, favoriteIds }).map((n) => ({
        ...n,
        favorite: favoriteIds.has(n.id),
      }));
      downloadCsv(TY_CSV_FILENAME, buildCsv(thankYouNotesCsvColumns, hydrated));
    } catch (error) {
      // Swallowing this would leave a click that silently did nothing — or worse, look like an
      // export that legitimately had no rows. The banner is the user's copy; the console keeps the
      // actual failure (a request error, or the walk's 10k-row cap).
      console.error("Thank-you notes CSV export failed", error);
      setExportError(TY_EXPORT_ERROR);
    } finally {
      setIsExporting(false);
    }
  }

  // REPORT/view is also granted to GFG roles, so the permission guard on the route alone lets a
  // GFG admin reach this client-scoped page by URL. Mirrors `CollectionsGate` in App.tsx.
  if (!isLoaded) return null;
  if (isGfg) return <Navigate to="/gfg-dashboard" replace />;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, fontFamily: "var(--font-lato)" }}>
      {isLoading ? (
        /* First load only: once a page has been fetched, `placeholderData` keeps it on screen and
           `isLoading` stays false, so a filter change fades the feed instead of blanking it. */
        <div className="animate-pulse h-64 bg-smoke rounded-lg" />
      ) : hasNoNotesInScope ? (
        /* Nothing to search or filter yet — and `unfilteredTotal` proves it, because it counts the
           caller's notes IGNORING the active filters. Only here is hiding the controls safe:
           hiding them on an empty *filtered* result would trap the user with no way to clear the
           filter that emptied the page. */
        <div className="flex border border-smoke rounded-[10px] bg-white p-4">
          <WidgetEmptyState
            icon={<DraftsOutlined />}
            title={TY_EMPTY_TITLE}
            subtitle={TY_EMPTY_SUBTITLE}
          />
        </div>
      ) : (
        <>
          <div className="flex items-center gap-[10px] flex-wrap mb-[14px]">
            <h2 className="font-crimson text-[22px] font-normal text-rodeo-black m-0">
              {THANK_YOU_NOTES_TITLE}
            </h2>
            {/* `role="status"` (implicit aria-live="polite") so a screen reader hears the count
                change as the user types in search or applies a filter — WCAG 4.1.3. Deliberately
                on the counter and the empty-message only: putting it on the list itself would
                make the reader recite every note on every keystroke.

                WHICH number, and why:
                - Favorites OFF → the server's `total`: every note matching the current search,
                  campaign and date filters. NOT `visible.length` — the feed holds only the pages
                  loaded so far (100 per page), so counting the cards on screen would announce
                  "100 notes" for a 412-note result and contradict the CSV export, which walks
                  every page.
                - Favorites ON → that toggle is applied in the browser and can only narrow the rows
                  already fetched, so `total` is not what the user is looking at and the favorited
                  count is not a total either. Both numbers are named instead.
                Hidden only when the error left nothing loaded: there is no response then, so any
                count would be invented. */}
            {/* Faded with the feed, not left bright above it: while a filter change is in flight the
                rows below are the PREVIOUS answer, and so is this count — announcing the old total
                at full opacity over dimmed rows reads as if the number were already current.
                `aria-busy` also holds the live region back until the new count is the real one. */}
            {!hasNothingToShow && (
              <span
                role="status"
                aria-busy={isStale}
                className={`text-[13px] text-graphite transition-opacity ${isStale ? "opacity-60" : ""}`}
              >
                {favOnly
                  ? tyFavoritesCountLabel(visible.length, notes.length)
                  : tyCountLabel(total)}
              </span>
            )}
          </div>

          <ThankYouNotesControls
            search={search}
            onSearchChange={handleSearchChange}
            filters={filters}
            onFiltersChange={setFilters}
            campaignOptions={campaignOptions}
            favOnly={favOnly}
            onToggleFavOnly={() => setFavOnly((v) => !v)}
            view={view}
            onViewChange={handleViewChange}
            onExport={handleExport}
            isExporting={isExporting}
          />

          {/* Above the feed, never in place of it — same rule the export error below follows. The
              request that failed is usually the one the CONTROLS just issued (an over-long search,
              a date range), so unmounting them would leave Retry re-firing the same bad request
              with no way to edit what caused it. */}
          {isError && (
            <div
              role="alert"
              className="flex items-center justify-between gap-3 flex-wrap text-good-wine bg-good-wine-bg rounded-lg p-4 mb-4"
            >
              <span className="text-[14px]">{TY_ERROR_MESSAGE}</span>
              <button
                type="button"
                onClick={handleRetry}
                className={`border border-good-wine text-good-wine hover:bg-white ${PAGE_BUTTON_BASE}`}
              >
                {TY_RETRY_LABEL}
              </button>
            </div>
          )}

          {exportError && (
            <div
              role="alert"
              className="text-good-wine bg-good-wine-bg rounded-lg p-4 mb-4 text-[14px]"
            >
              {exportError}
            </div>
          )}

          {/* The feed area is the only thing a list error ever replaces, and only when the error
              left no rows at all: "No thank-you notes match these filters." would otherwise blame
              the filters for a failed request. */}
          {!hasNothingToShow && (
            <>
              <div
                aria-busy={isStale}
                className={`transition-opacity ${isStale ? "opacity-60" : ""}`}
              >
                {visible.length === 0 ? (
                  <div role="status" className="py-6 text-center text-[14px] text-graphite">
                    {/* Favorites is the only filter applied here, so it is the reason the feed is
                        empty only when the server did return rows; with none back, the search or
                        the campaign/date filters emptied it. */}
                    {favOnly && notes.length > 0 ? TY_NO_FAVORITES : TY_NO_MATCH}
                  </div>
                ) : view === "grid" ? (
                  <div className="ty-grid">
                    {visible.map((note) => (
                      <ThankYouNoteCard
                        key={note.id}
                        note={note}
                        isFavorite={favoriteIds.has(note.id)}
                        onToggleFavorite={toggleFavorite}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-[10px]">
                    {visible.map((note) => (
                      <ThankYouNoteRow
                        key={note.id}
                        note={note}
                        isFavorite={favoriteIds.has(note.id)}
                        onToggleFavorite={toggleFavorite}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Only while the server says there is more (`nextCursor !== null`). Disabled on any
                  in-flight fetch, not just the next page: mid-filter-change the rows on screen are
                  the PREVIOUS result, and paging that would append rows the current filter never
                  matched. */}
              {hasNextPage && (
                <div className="flex justify-center mt-4">
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={isFetching}
                    className={`border border-dark-dust text-rodeo-black hover:bg-wax disabled:opacity-60 disabled:cursor-not-allowed ${PAGE_BUTTON_BASE}`}
                  >
                    {isFetchingNextPage ? TY_LOADING_MORE_LABEL : TY_LOAD_MORE_LABEL}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
}
